/**
 * Every editable string in the application, and what it says now.
 *
 * THE BACKSTOP, not the primary way in. Changing a word is easiest on the
 * screen it appears on, which is what edit-on-the-page is for. This exists for
 * the text nobody can navigate to: error messages, empty states, the
 * confirmation that only appears after a deletion. Those are exactly where bad
 * wording survives longest, because a reviewer never encounters them by
 * browsing.
 *
 * IT LISTS WHAT THE SOURCE CONTAINS, not what the database does. The catalogue
 * is generated at build time by reading the code, so this screen shows every
 * string that exists, with the stored overrides layered on top. A string
 * nobody has edited still appears, with its shipped wording — which is what
 * makes the export useful as a review document rather than a changelog.
 *
 * Front Porch administrators only. The route is behind the same check as
 * Settings and Users, and the API refuses regardless of what the interface
 * chooses to draw.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface CopyString {
  key: string;
  screen: string;
  /** The wording in the source. What a cleared override falls back to. */
  shipped: string;
  /** What it says now: the override if there is one, otherwise `shipped`. */
  value: string;
  changed: boolean;
  /** Placeholders this text can use, e.g. ['{n}']. */
  vars: string[];
  /** Those of `vars` the sentence can do without. Anything else must stay. */
  optional: string[];
  /** How many places call it. More than one and an edit moves all of them. */
  uses: number;
  where: string[];
  changedAt: string | null;
  changedBy: string | null;
}

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function TextSettings() {
  const [strings, setStrings] = useState<CopyString[] | null>(null);
  const [orphaned, setOrphaned] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  /** Which key is open for editing, and the text in its box. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  /** The open editor's textarea, so a variable lands at the caret rather than
   *  being appended somewhere the writer was not looking. */
  const box = useRef<HTMLTextAreaElement>(null);

  /**
   * Puts a variable where the caret is.
   *
   * The same gesture the email template editor has always offered. Selecting
   * text and clicking a variable replaces the selection, which is how somebody
   * swaps one for another without deleting first.
   */
  function insertVar(token: string) {
    const el = box.current;
    if (!el) {
      setDraft((d) => d + token);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    setDraft(el.value.slice(0, start) + token + el.value.slice(end));
    /* After React has painted the new value, or the caret jumps to the end. */
    window.setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  }

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/content');
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { strings: CopyString[]; orphaned: string[] };
      setStrings(body.strings);
      setOrphaned(body.orphaned);
    } catch {
      setError('Could not load the text catalogue.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(key: string) {
    setSaving(true);
    setProblem(null);
    try {
      const res = await fetch(`/api/content/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: draft }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        /* Shown against the field rather than at the top of the page. The
           refusals here are about this string — a missing variable, an em dash
           — and a banner would make the reader hunt for which one. */
        setProblem(body.error ?? 'That did not save.');
        return;
      }
      setEditing(null);
      await load();
    } catch {
      setProblem('That did not save. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function revert(key: string) {
    setSaving(true);
    try {
      await fetch(`/api/content/${encodeURIComponent(key)}`, { method: 'DELETE' });
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  }

  const visible = useMemo(() => {
    if (!strings) return [];
    const q = search.trim().toLowerCase();
    return strings.filter((s) => {
      if (onlyChanged && !s.changed) return false;
      if (!q) return true;
      return (
        s.key.toLowerCase().includes(q) ||
        s.value.toLowerCase().includes(q) ||
        s.shipped.toLowerCase().includes(q)
      );
    });
  }, [strings, search, onlyChanged]);

  /** Grouped by screen, in the order the catalogue lists them. */
  const groups = useMemo(() => {
    const out = new Map<string, CopyString[]>();
    for (const s of visible) {
      const list = out.get(s.screen) ?? [];
      list.push(s);
      out.set(s.screen, list);
    }
    return [...out.entries()];
  }, [visible]);

  const changedCount = strings?.filter((s) => s.changed).length ?? 0;

  if (error) return <p className="auth-error">{error}</p>;
  if (!strings) return <div className="app-loading" aria-busy="true" />;

  if (strings.length === 0) {
    return (
      <div className="empty">
        <p className="empty-title">No catalogue yet</p>
        <p className="empty-copy">
          The catalogue is generated from the source when the application is built. This deployment
          has not produced one, so there is nothing to list. Every screen still shows its shipped
          wording.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="phead">
        <div>
          <p className="eyebrow">Front Porch Solutions · platform</p>
          <h1 className="title">Text</h1>
          <p className="lede">
            Every word the application can show, and what it says now. Changing one here changes it
            everywhere that string is used, without a deploy.
          </p>
        </div>
      </div>

      {/* An override whose string no longer exists in the code. Surfaced rather
          than hidden: the alternative is a row nobody can see and nobody can
          clear, which is how a content table silently fills with rubbish. */}
      {orphaned.length > 0 && (
        <div className="notice">
          <b>
            {orphaned.length} edited {orphaned.length === 1 ? 'string is' : 'strings are'} no longer
            in the application.
          </b>
          <p className="fdesc" style={{ margin: '4px 0 8px' }}>
            The wording was changed, then the screen that used it was rewritten. Nothing is showing
            these, and clearing them is safe.
          </p>
          <div className="handoff">
            {orphaned.map((key) => (
              <button
                key={key}
                type="button"
                className="btn-compact btn-compact--quiet"
                disabled={saving}
                onClick={() => void revert(key)}
              >
                Clear {key}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="search">
          <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            className="search-input"
            placeholder="Search words or keys…"
            aria-label="Search the text catalogue"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={`chip${onlyChanged ? ' on' : ''}`}
          aria-pressed={onlyChanged}
          onClick={() => setOnlyChanged((v) => !v)}
        >
          Changed only{changedCount > 0 ? ` (${changedCount})` : ''}
        </button>

        <div className="toolbar-gap" />

        <button
          type="button"
          className="btn-compact btn-compact--quiet"
          onClick={() => setShowExport((v) => !v)}
        >
          {showExport ? 'Hide Export' : 'Export For Review'}
        </button>
      </div>

      {/* The document a language reviewer actually works from. Nobody is going
          to click three hundred strings in a browser, and the ones most in need
          of review are the ones hardest to reach on screen. */}
      {showExport && <Export strings={strings} />}

      {visible.length === 0 ? (
        <div className="empty">
          <p className="empty-title">Nothing matches</p>
          <p className="empty-copy">
            No string matches that search{onlyChanged ? ' among the ones that have been changed' : ''}.
          </p>
        </div>
      ) : (
        groups.map(([screen, items]) => (
          <div className="panel" key={screen}>
            <div className="panel-head">
              <h3>{screen}</h3>
              <span className="hint">
                {items.length} {items.length === 1 ? 'string' : 'strings'}
              </span>
            </div>
            <div className="panel-body">
              <ul className="cstr">
                {items.map((s) => (
                  <li key={s.key} className={s.changed ? 'is-changed' : undefined}>
                    <div className="cstr-head">
                      <code className="cstr-key">{s.key}</code>
                      {s.changed && (
                        <span className="badge b-warning">
                          <span className="d" />
                          Changed
                        </span>
                      )}
                    </div>

                    {editing === s.key ? (
                      <>
                        <textarea
                          ref={box}
                          className="fi ft"
                          rows={2}
                          autoFocus
                          value={draft}
                          disabled={saving}
                          onChange={(e) => setDraft(e.target.value)}
                        />
                        <p className="fdesc">
                          Shipped: <b>{s.shipped}</b>
                        </p>
                        {/* Said because it is not obvious and the first real
                            edit got caught by it: a line break typed here used
                            to vanish, since HTML collapses newlines. */}
                        <p className="fdesc">A line break here shows on the page.</p>
                        {s.where.length > 1 && (
                          <p className="cstr-flag">
                            Also on {s.where.length - 1} other{' '}
                            {s.where.length === 2 ? 'screen' : 'screens'}, and they all change
                            together: {s.where.join(', ')}.
                          </p>
                        )}
                        {/* WHAT THIS TEXT CAN USE, offered rather than merely
                            policed. The first version of this screen named the
                            variables only to say they could not be removed,
                            which told somebody what they were forbidden from
                            doing and not what they were allowed to do. The
                            email template editor has offered its fields for
                            insertion since it was built; this is the same
                            idea, late. */}
                        {s.vars.length > 0 && (
                          <div className="cstr-vars">
                            <span className="cstr-vars-label">Insert</span>
                            {s.vars.map((v) => {
                              const required = !s.optional.includes(v);
                              return (
                                <button
                                  key={v}
                                  type="button"
                                  className={`cstr-var${required ? ' is-required' : ''}`}
                                  title={
                                    required
                                      ? 'This one cannot be removed: its value has nowhere else to appear'
                                      : 'Optional. The sentence reads correctly without it'
                                  }
                                  onClick={() => insertVar(v)}
                                >
                                  {v}
                                </button>
                              );
                            })}
                            <span className="cstr-vars-note">
                              {s.vars.every((v) => s.optional.includes(v))
                                ? 'All optional.'
                                : `${s.vars.filter((v) => !s.optional.includes(v)).join(' and ')} must stay.`}
                            </span>
                          </div>
                        )}
                        {problem && <p className="auth-error">{problem}</p>}
                        <div className="cstr-actions">
                          <button
                            type="button"
                            className="btn-compact"
                            disabled={saving}
                            onClick={() => void save(s.key)}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn-compact btn-compact--quiet"
                            disabled={saving}
                            onClick={() => {
                              setEditing(null);
                              setProblem(null);
                            }}
                          >
                            Cancel
                          </button>
                          {s.changed && (
                            <button
                              type="button"
                              className="cstr-revert"
                              disabled={saving}
                              onClick={() => void revert(s.key)}
                            >
                              Reset to what shipped
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="cstr-value"
                        onClick={() => {
                          setEditing(s.key);
                          setDraft(s.value);
                          setProblem(null);
                        }}
                      >
                        <span className="cstr-now">{s.value}</span>
                        {s.changed && (
                          <span className="cstr-was">
                            Shipped: <s>{s.shipped}</s>
                            {s.changedBy ? ` · changed by ${s.changedBy}` : ''}
                            {s.changedAt ? ` · ${when(s.changedAt)}` : ''}
                          </span>
                        )}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))
      )}

      <p className="tbl-foot">
        Showing {visible.length} of {strings.length} strings ·{' '}
        {changedCount === 0 ? 'none changed' : `${changedCount} changed`}
      </p>
    </>
  );
}

/**
 * The catalogue as plain text, for somebody reviewing the language.
 *
 * DELIBERATELY NOT A SPREADSHEET OR A FORM. The person doing this is marking up
 * wording, not operating software, and what they hand back is a document with
 * changes on it. Grouped by screen so related strings are read together, which
 * is the only way inconsistency between them becomes visible: four ways of
 * saying "not invited" look fine one at a time and obviously wrong in a list.
 */
function Export({ strings }: { strings: CopyString[] }) {
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    const byScreen = new Map<string, CopyString[]>();
    for (const s of strings) {
      const list = byScreen.get(s.screen) ?? [];
      list.push(s);
      byScreen.set(s.screen, list);
    }

    const lines: string[] = [
      'FPS SPONSOR LOGOS: COPY FOR REVIEW',
      `Generated ${new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })} · ${strings.length} strings`,
      '',
    ];

    for (const [screen, items] of byScreen) {
      lines.push(`${'='.repeat(60)}`, screen.toUpperCase(), '='.repeat(60), '');
      for (const s of items) {
        lines.push(`  ${s.key}`);
        lines.push(`  NOW      ${s.value}`);
        if (s.changed) lines.push(`  SHIPPED  ${s.shipped}`);
        if (s.vars.length > 0) lines.push(`  KEEP     ${s.vars.join(' ')}`);
        if (s.uses > 1) lines.push(`  NOTE     used in ${s.uses} places`);
        lines.push('');
      }
    }

    return lines.join('\n');
  }, [strings]);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Copy for review</h3>
        <span className="hint">grouped by screen, in plain text</span>
      </div>
      <div className="panel-body">
        <p className="fdesc" style={{ marginTop: 0 }}>
          Send this to whoever is reviewing the language. It covers every string, including the ones
          that only appear in an error or an empty state and would never be found by clicking
          around.
        </p>
        <div className="handoff" style={{ marginBottom: 'var(--sp3)' }}>
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            onClick={() => {
              void navigator.clipboard?.writeText(text);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? 'Copied' : 'Copy All'}
          </button>
        </div>
        <pre className="cstr-export">{text}</pre>
      </div>
    </div>
  );
}
